import { createTheme, type Theme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    focusVisible: Palette['primary'];
  }
  interface PaletteOptions {
    focusVisible?: PaletteOptions['primary'];
    white?: PaletteOptions['primary'];
  }
}

export const blkTheme = createTheme({
  palette: {
    primary: {
      main: '#0d1523',
      light: '#121e31',
    },
    secondary: {
      main: '#ffe002',
      light: '#f6f6f6',
    },
    error: {
      main: '#c81e33',
    },
    warning: {
      main: '#ef9825',
    },
    success: {
      main: '#6FB469',
      contrastText: '#fff',
    },
    info: {
      main: '#2D3C55',
    },
    text: {
      primary: 'rgba(19, 30, 48, 1)',
      secondary: '#6B6B6B',
    },
    background: {
      default: '#fff',
    },
    focusVisible: {
      main: '#1e88e5',
    },
  },
  cssVariables: true,
  typography: {
    fontFamily: ['Akkurat pro', 'sans-serif'].join(','),
    h4: { fontFamily: ['Stratum1Bold', 'sans-serif'].join(','), textTransform: 'uppercase' },
    h6: { fontFamily: ['Stratum1Bold', 'sans-serif'].join(','), textTransform: 'uppercase' },
  },
  components: {
    MuiFormControlLabel: {
      styleOverrides: {
        label: { fontSize: '0.875rem' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '8px 16px 24px',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          padding: '13px 20px 10px',
          fontSize: '0.75rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '&:active': { boxShadow: 'none' },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.focusVisible.main}`,
            zIndex: 10,
          },
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          '@media (max-width: 320px)': {
            padding: '8px 12px',
          },

          variants: [
            {
              props: { color: 'primary' },
              style: {
                '&:hover': {
                  backgroundColor: '#FFE000',
                  color: 'black',
                },
              },
            },
          ],
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#0d1523',
          color: 'white',
          top: 'inherit', // Sidenav starts beneath topnavbar
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'white',
            borderRadius: 10,
          },
          '&:active': {
            backgroundColor: 'white',
            borderRadius: 10,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '.Mui-disabled': { opacity: 0.5 },
          '.MuiTypography-body1': {
            paddingLeft: '45px',
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: 'white !important',
          '&:hover': {
            color: 'white !important',
          },
          '&.Mui-active': {
            color: 'white !important',
          },
        },
        icon: {
          color: 'white !important',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: ['Stratum1Bold', 'sans-serif'].join(','),
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '.MuiTablePagination-root .MuiInputBase-root &': {
            top: 'calc(50% - .55em)',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'black',
          '&.Mui-focused': {
            color: 'black',
          },
          '&.MuiInputLabel-root': {
            transform: 'translate(14px, 9px) scale(0.875)',
          },
          '&.MuiInputLabel-root.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
          '.MuiTablePagination-root &': {
            transform: 'translate(14px, 0px) scale(0.875)',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
        sizeSmall: {
          minHeight: '40px',
        },
      },
    },
    MuiIconButton: {
      defaultProps: { color: 'primary' },
      styleOverrides: {
        root: {
          border: '0px',
          outline: 'none',
          '&:active': {
            outline: 'none',
            border: '0px',
          },
          '&:hover': {
            outline: 'none',
            border: '0px',
            backgroundColor: 'transparent',
          },
          '&:focus': {
            outline: 'none',
            border: '0px',
          },
        },
      },
    },
  },
});
